import logger from '@eventbrite/client-logger';
import { HAS_WINDOW } from '@eventbrite/feature-detection';
import { sdkRequest } from '@eventbrite/http';
import { gettext } from '@eventbrite/i18n';
import { formatMajorMoney } from '@eventbrite/intl';
import { intercomUpdate } from '@eventbrite/marketing-tools-common';
import { differenceInYears, isFuture, isValid, subYears } from 'date-fns';
import {
    EB_14_DAY_TRIAL_100_PACKAGE,
    EB_14_DAY_TRIAL_250_PACKAGE,
    EB_14_DAY_TRIAL_UNLIM_PACKAGE,
    FLEX_FREE_TIER_CAPACITY,
    PERMISSION_ORGANIZATION_BILLING_PLAN_MANAGE,
    PERMISSION_ORGANIZATION_BILLING_PLAN_READ,
    PRO_100_CAPACITY_MAX,
    PRO_100_PLAN_NAME,
    PRO_250_CAPACITY_MAX,
    PRO_250_PLAN_NAME,
    PRO_UNLIMITED_PLAN_NAME,
    SUBSCRIPTION_STATUS_ACTIVE,
    SUBSCRIPTION_STATUS_DELINQUENT,
    SUBSCRIPTION_STATUS_INCOMPLETE,
    SUBSCRIPTION_STATUS_TRIALING,
    UNLIMITED_CAPACITY,
} from './constants';
import { InvoiceChargeInfo } from './invoices';
import {
    BillingPeriodInterval,
    fetchOrganizationPlan,
    getOrganizationPlanDetails,
    getSubscriptionAndTrialStatus,
    PlanDetailsObject,
    PlanOption,
    SubscriptionAndTrialStatus,
    SubscriptionStatusInfo,
} from './plans';
import { fetchOrganizationSubscription } from './subscriptions';
import { fetchPPTrial } from './trials';
import { PricingAndPackagingFeatureFlags } from './types';

export const determineLocaleFromTld = (url: string) => {
    const tld = url.split('.').pop();

    switch (tld) {
        case 'ca':
            return 'CA';
        case 'uk':
            return 'GB';
        case 'ie':
            return 'IE';
        case 'au':
            return 'AU';
        case 'nz':
            return 'NZ';
        default:
            return 'US';
    }
};

export const getPlaceFromRequest = () => {
    const GET_PLACE_FROM_IP = '/api/v3/geo/place_from_request/';

    return sdkRequest(GET_PLACE_FROM_IP);
};

export const getISODate = (timestamp: number | null) => {
    if (!timestamp) return undefined;
    const timestampToUse = isMilliseconds(timestamp)
        ? timestamp
        : timestamp * 1000;

    return new Date(timestampToUse).toISOString();
};

export const isMilliseconds = (value: number): boolean => {
    // Check if the number is a positive integer
    if (!Number.isInteger(value) || value < 0) {
        return false;
    }

    const date = new Date(value);
    const epoch = new Date(0); // 1970-01-01T00:00:00.000Z

    // Check if it's a valid date and more than 50 years from the epoch
    if (!isValid(date) || differenceInYears(date, epoch) < 50) {
        return false;
    }

    // Check if it's not too far in the future (e.g., not more than 100 years)
    if (isFuture(subYears(date, 100))) {
        return false;
    }

    return true;
};

export const getTermsPageUrl = () => {
    let url = `https://www.eventbrite.com/l/merchant-agreement/`;

    if (window) {
        const origin = window.location.origin;

        let tldLocale = determineLocaleFromTld(window.location.hostname);

        if (tldLocale) {
            tldLocale = `en-${tldLocale}`;
        }

        const tld = origin.split('eventbrite.')?.[1] || 'com';

        url = `https://www.eventbrite.${tld}/l/merchant-agreement/`;
    }

    return url;
};

export const getSupportUrl = () => {
    let url = `https://www.eventbrite.com/help/en-us/contact-us/`;

    if (window) {
        const origin = window.location.origin;

        let tldLocale = determineLocaleFromTld(window.location.hostname);

        if (tldLocale) {
            tldLocale = `en-${tldLocale}`;
        }

        let locale: string =
            window.__SERVER_DATA__?.locale || tldLocale || 'en_US';
        locale = locale.replace('_', '-').toLowerCase();

        const tld = origin.split('eventbrite.')?.[1] || 'com';

        url = `https://www.eventbrite.${tld}/help/${locale}/contact-us/`;
    }

    return url;
};

export const getTimestampInSeconds = (dateInfo: string | number) => {
    if (
        !dateInfo ||
        (typeof dateInfo !== 'string' && typeof dateInfo !== 'number')
    ) {
        return null;
    }

    if (typeof dateInfo === 'number' && dateInfo.toString().length === 10) {
        return dateInfo;
    } else {
        const dateObject = dateInfo === 'now' ? new Date() : new Date(dateInfo);
        const secondsTimestamp = Math.round(dateObject.getTime() / 1000);

        return secondsTimestamp;
    }
};

export const getTld = () => {
    if (window?.__SERVER_DATA__?.env?.localeInfo?.tld) {
        return window?.__SERVER_DATA__?.env?.localeInfo?.tld;
    } else if (window?.location?.hostname) {
        const hostname = window.location.hostname;

        const tld = hostname.split('.').pop();

        switch (tld) {
            case 'ca':
                return '.ca';
            case 'uk':
                return '.co.uk';
            case 'ie':
                return '.ie';
            case 'au':
                return '.co.au';
            case 'nz':
                return '.co.nz';
            default:
                return '.com';
        }
    } else {
        return '.com';
    }
};

export const sendUpdateToIntercom = async (intercomUpdateObject: any) => {
    try {
        const tld = getTld();

        const locale = window?.__SERVER_DATA__?.locale;

        if (locale) {
            intercomUpdateObject.pp_language_code = locale;
        }

        intercomUpdateObject.pp_organization_tld = tld;
        intercomUpdateObject.pp_subscription_management_url = `https://www.eventbrite${tld}/organizations/plan-management`;
        intercomUpdateObject.pp_subscription_terms_url = getTermsPageUrl();

        intercomUpdate(intercomUpdateObject);
    } catch (error) {
        logger.error(error, {
            message: 'Error sending update to Intercom in P&P API library:',
        });
    }

    return;
};

export const convertSecondsToMilliseconds = (value: number) => {
    const digits = value.toString().length;

    // 13 digits mean valid timestamp in ms
    if (digits === 13) {
        return value;
    }

    // 10 digits means we were passed a timestamp in seconds
    if (digits === 10) {
        return value * 1000;
    } else return 0;
};

export const getUserLocaleAndCurrency = () => {
    let userLocale = 'US';
    let currency = 'USD';

    if (window?.__SERVER_DATA__?.locale) {
        userLocale = window?.__SERVER_DATA__?.locale.split('_').pop();
    } else if (HAS_WINDOW) {
        userLocale = determineLocaleFromTld(window.location.hostname);
    }

    switch (userLocale) {
        case 'CA':
            currency = 'CAD';
            break;
        case 'GB':
            currency = 'GBP';
            break;
        case 'IE':
            currency = 'EUR';
            break;
        case 'AU':
            currency = 'AUD';
            break;
        case 'NZ':
            currency = 'NZD';
            break;
        default:
            currency = 'USD';
            break;
    }

    return { userLocale, currency };
};

export const getOrganizationSubscriptionAndTrial = async (
    organizationId: string,
    options?: { featureFlags?: PricingAndPackagingFeatureFlags },
): Promise<SubscriptionStatusInfo> => {
    let planDetails: PlanDetailsObject | null;
    let subAndTrialStatus: SubscriptionAndTrialStatus;

    try {
        const { currency } = getUserLocaleAndCurrency();

        const [subscription, plan, trial] = await Promise.all([
            fetchOrganizationSubscription(organizationId, {
                featureFlags: options?.featureFlags,
            }),
            fetchOrganizationPlan(organizationId, {
                featureFlags: options?.featureFlags,
            }),
            fetchPPTrial(organizationId),
        ]);

        let currencyForPlan = currency;

        if (
            subscription?.status &&
            [
                SUBSCRIPTION_STATUS_ACTIVE,
                SUBSCRIPTION_STATUS_DELINQUENT,
                SUBSCRIPTION_STATUS_INCOMPLETE,
                SUBSCRIPTION_STATUS_TRIALING,
            ].includes(subscription.status)
        ) {
            currencyForPlan = subscription.currency;
        }

        planDetails = getOrganizationPlanDetails(
            plan,
            subscription,
            currencyForPlan,
            { featureFlags: options?.featureFlags },
        );
        subAndTrialStatus = getSubscriptionAndTrialStatus({
            subscription,
            trial,
            options,
        });

        if (planDetails) {
            const intercomUpdateObject = {} as any;

            if (planDetails.currency) {
                intercomUpdateObject.pp_currency = planDetails.currency;

                if (planDetails.next_invoice_amount) {
                    intercomUpdateObject.pp_subscription_next_charge_amount =
                        formatMajorMoney(
                            planDetails.next_invoice_amount,
                            planDetails.currency.toUpperCase(),
                        );
                }
            }

            if (planDetails.next_payment_date) {
                intercomUpdateObject.pp_subscription_next_charge_at =
                    getTimestampInSeconds(planDetails.next_payment_date);
            }

            if (planDetails.features) {
                intercomUpdateObject.pp_current_ticket_limit = isNaN(
                    //eslint-disable-next-line
                    //@ts-ignore
                    planDetails.features.event_capacity,
                )
                    ? gettext('unlimited')
                    : `${planDetails.features.event_capacity}`;
                intercomUpdateObject.pp_email_daily_send_limit =
                    planDetails.features.campaign_daily_limit;
            }

            if (!planDetails.trial_expiration_date) {
                intercomUpdateObject.pp_subscription_pro_plan_name =
                    planDetails.plan_name;
            }

            sendUpdateToIntercom(intercomUpdateObject);
        }

        return {
            subscription: planDetails,
            subAndTrialStatus: subAndTrialStatus,
        };
    } catch (error) {
        throw error;
    }
};

export const calculatePermission = async (
    organizationId: string,
    permission: string,
) => {
    try {
        const response = await sdkRequest('/api/v3/permissions/calculate/', {
            method: 'POST',
            body: JSON.stringify({
                entities: [
                    {
                        type: 'user',
                        id: organizationId,
                    },
                ],
                permissions: [permission],
            }),
            headers: {
                'Content-Type': 'application/json',
            },
        });

        const isPermissionGranted =
            response.entity_perms[0].permissions.includes(permission);
        return isPermissionGranted;
    } catch (error) {
        logger.error(error, {
            message: `Error calculating permission ${permission} for organization ${organizationId}`,
        });
        throw error;
    }
};

export const calculateOrganizationBillingReadPermission = async (
    organizationId: string,
) => {
    return await calculatePermission(
        organizationId,
        PERMISSION_ORGANIZATION_BILLING_PLAN_READ,
    );
};

export const calculateOrganizationBillingManagePermission = async (
    organizationId: string,
) => {
    return await calculatePermission(
        organizationId,
        PERMISSION_ORGANIZATION_BILLING_PLAN_MANAGE,
    );
};

export const currencyFormatter = (
    currency: string,
    price: number,
    leaveDecimals?: boolean,
) => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
    });
    let formatted = formatter.format(price);
    if (formatted.includes('.00') && !leaveDecimals) {
        formatted = formatted.slice(0, -3);
    }
    return formatted;
};

export const elfCurrencyFormatter = (
    currency: string,
    price: number,
    locale?: string,
) => {
    // Convert price to dollars (or relevant currency unit, assuming price is in cents)
    const adjustedPrice = price / 100;

    // Format the price with appropriate decimal places for the currency
    let formattedPrice = Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(adjustedPrice);

    // Get the decimal separator for the currency
    const decimalSeparator = formattedPrice[formattedPrice.length - 3];

    // Remove decimals if the price is a whole number
    if (formattedPrice.endsWith(`${decimalSeparator}00`)) {
        formattedPrice = formattedPrice.slice(0, -3);
    }

    return formattedPrice;
};

export const getPlanPricingObject = (
    plan?: PlanOption | null,
    currency?: string,
    billingPeriodInterval: BillingPeriodInterval = 'month',
) => {
    if (plan?.pricing?.length === 1) {
        return plan?.pricing?.[0];
    } else {
        const planPricingObject = plan?.pricing?.find(
            (pricingOption) =>
                pricingOption?.period?.interval === billingPeriodInterval &&
                pricingOption?.currency.toLowerCase() ===
                    currency?.toLowerCase(),
        );

        return planPricingObject;
    }
};

export const mapPlanNameToPackageId = (planName: string) => {
    switch (planName) {
        case PRO_100_PLAN_NAME:
            return EB_14_DAY_TRIAL_100_PACKAGE.toString();
        case PRO_250_PLAN_NAME:
            return EB_14_DAY_TRIAL_250_PACKAGE.toString();
        case PRO_UNLIMITED_PLAN_NAME:
            return EB_14_DAY_TRIAL_UNLIM_PACKAGE.toString();
        default:
            return EB_14_DAY_TRIAL_100_PACKAGE.toString();
    }
};

export const getHostedInvoiceUrl = (
    subscriptionData?: PlanDetailsObject | null,
) => {
    const { latest_invoice } = subscriptionData || {};
    const { hosted_invoice_url } = (latest_invoice as InvoiceChargeInfo) || {};

    return hosted_invoice_url;
};

export const getInvoiceFailureCode = (
    subscriptionData?: PlanDetailsObject | null,
) => {
    const { latest_invoice } = subscriptionData || {};
    const { failure_code } = (latest_invoice as InvoiceChargeInfo) || {};

    return failure_code;
};

export const getFlexCapacityFromRequest = (capacityRequest: number) => {
    if (capacityRequest <= FLEX_FREE_TIER_CAPACITY) {
        return FLEX_FREE_TIER_CAPACITY;
    } else if (capacityRequest <= PRO_100_CAPACITY_MAX) {
        return PRO_100_CAPACITY_MAX;
    } else if (capacityRequest <= PRO_250_CAPACITY_MAX) {
        return PRO_250_CAPACITY_MAX;
    } else {
        return UNLIMITED_CAPACITY;
    }
};

export const getFutureDate = (days: number) => {
    const result = new Date();
    return new Date(result.getDate() + days);
};
